import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Shared hook that asks the backend whether the current user can publish to
 * a given platform RIGHT NOW. Returns a stable shape so callers can render
 * disabled state + inline messages without scattering business logic across
 * the UI.
 *
 *   const { eligibility, loading, refresh } = usePublishEligibility(platform);
 *   eligibility = {
 *     can_publish: boolean,
 *     connected: boolean,
 *     mode: "real" | "demo" | null,
 *     requires_review: boolean,
 *     limit_reached: boolean,
 *     monthly_limit, monthly_used, monthly_remaining, plan,
 *     message: string|null,
 *     cta: { label, href } | null,
 *   }
 *
 * Pass `enabled=false` to skip the network call (useful for already-published
 * posts where the button shouldn't render at all).
 */
export default function usePublishEligibility(platform, { enabled = true } = {}) {
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(!!enabled && !!platform);

  const refresh = useCallback(async () => {
    if (!enabled || !platform) {
      setEligibility(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get("/posts/publish-eligibility", {
        params: { platform },
      });
      setEligibility(res.data || null);
    } catch (_e) {
      // Soft-fail: assume blocked but don't crash the UI.
      setEligibility({
        can_publish: false,
        connected: false,
        requires_review: false,
        limit_reached: false,
        message: "Could not verify publishing readiness. Try again in a moment.",
        cta: null,
      });
    } finally {
      setLoading(false);
    }
  }, [platform, enabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { eligibility, loading, refresh };
}
